import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Grid from 'epn-ui/umd/dist/components/Grid';
import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';
import isEmpty from 'lodash/isEmpty';
import { toast } from 'react-toastify';

import BaseToast from '@base/BaseToast';
import BaseSemanticLink from '@components/base/BaseSemanticLink';

import useI18n from '@i18n';
import copyTextToClipboard from '@utils/clipboard';
import { TITLES } from '../config';
import { IResult, ResultTypes } from '../index.d';

const Result: React.FC<IResult> = ({ type, data }) => {
    const { t } = useI18n('LinkChecker');
    const { t: tCommon } = useI18n('Common');

    if (isEmpty(data)) return null;

    const copyLink = async (link: string) => {
        await copyTextToClipboard(link);
        toast.success(<BaseToast text={tCommon('Copied')} type="success" />);
    };

    const getRow = ({ link, cashbackCis, cashbackHotsale, cashbackNotCis }) => (
        <Grid
            key={`${type}${link}`}
            gutter={[16, 16]}
            className={clsx('rounded-[6px] bg-opacity-50 p-[16px] text-blueGray-800 mb-[4px]', {
                'bg-sky-100': type === ResultTypes.affiliate,
                'bg-red-100':
                    type === ResultTypes.notAffiliate || type === ResultTypes.notParticipate,
                'bg-blueGray-100': type === ResultTypes.wrong,
            })}
        >
            {/* Ссылка */}
            <Grid.Col
                span={24}
                lg={type !== ResultTypes.wrong ? 12 : 24}
                className="flex flex-row items-center"
            >
                <div className="flex flex-nowrap">
                    <BaseSemanticLink
                        to={link}
                        className="self-start underline break-all text-blueGray-800"
                    >
                        {link}
                    </BaseSemanticLink>
                    <button
                        type="button"
                        className=" self-start px-2 cursor-pointer text-p2 text-blueGray-500 transition-colors duration-200 in-out hover:text-blueGray-800"
                        onClick={() => copyLink(link)}
                    >
                        <FontAwesomeIcon icon={faCopy} />
                        <span className="sr-only">{t('Copy')}</span>
                    </button>
                </div>
            </Grid.Col>

            {/* Список ставок */}
            {type !== ResultTypes.wrong && type !== ResultTypes.notParticipate && (
                <Grid.Col
                    span={24}
                    lg={12}
                    className="flex-wrap justify-start lg:justify-end gap-[16px] xs:gap-0 xs:flex-nowrap xs:space-x-6 xs:flex-row !flex"
                >
                    {/* Кэшбэк для стран СНГ */}
                    <div>
                        <p>
                            {t('Product rate for CIS countries')}{' '}
                            <TooltipPro
                                title={t(
                                    'Rate for the product, taking into account the additional bonus for using the article number',
                                )}
                            >
                                <FontAwesomeIcon
                                    className="cursor-pointer mr-1.5 text-blueGray-500"
                                    icon={faQuestionCircle}
                                />
                            </TooltipPro>
                            :
                        </p>
                        <p className="font-medium">
                            {t('Up to')} {cashbackCis}
                        </p>
                    </div>

                    {/* Кэшбэк для других стран */}
                    <div>
                        <p>{t('Product Rade Worldwide')}:</p>
                        <p className="font-medium">{cashbackNotCis}</p>
                    </div>

                    {/* Процент Hotsale */}
                    {cashbackHotsale !== '0%' && (
                        <div>
                            <p>
                                <TooltipPro
                                    title={t(
                                        'Hotsale product rate is applied only if the user clicks',
                                    )}
                                >
                                    <FontAwesomeIcon
                                        className="cursor-pointer mr-1.5 text-blueGray-500"
                                        icon={faQuestionCircle}
                                    />
                                </TooltipPro>
                                Hotsale:
                            </p>
                            <p className="font-medium">{cashbackHotsale}&nbsp;&#128293;</p>
                        </div>
                    )}
                </Grid.Col>
            )}
        </Grid>
    );

    const rows = data.map(getRow);

    return (
        <div>
            <h5
                className={clsx('mb-3 font-medium text-p1 text-blueGray-500', {
                    'text-red-400': type === ResultTypes.notParticipate,
                })}
            >
                {t(TITLES[type])}
            </h5>
            <div>{rows}</div>
        </div>
    );
};

export default Result;
